/** Locale-aware, layout-stable formatters for every numeric readout. */

/** 0.8734 -> "87.34" (percent value without the sign, for tabular readouts). */
export function pct(value, digits = 2) {
  if (!Number.isFinite(value)) return "—";
  return (value * 100).toFixed(digits);
}

/** 0.8734 -> "87.34%" */
export function pctLabel(value, digits = 2) {
  if (!Number.isFinite(value)) return "—";
  return `${pct(value, digits)}%`;
}

/**
 * Signed delta in percentage points: 0.46 -> "+46.0 pp".
 *
 * Values that round to zero at the requested precision are printed unsigned,
 * so a negligible negative difference never renders as "−0.0 pp".
 */
export function deltaLabel(value, digits = 1) {
  if (!Number.isFinite(value)) return "—";
  const points = value * 100;
  const rounded = Number(points.toFixed(digits));
  const sign = rounded > 0 ? "+" : rounded < 0 ? "−" : "";
  return `${sign}${Math.abs(points).toFixed(digits)} pp`;
}

/** Below `epsilon` (0.5 pp by default) a difference is reported as flat. */
export function deltaDirection(value, epsilon = 0.005) {
  if (!Number.isFinite(value) || Math.abs(value) < epsilon) return "flat";
  return value > 0 ? "up" : "down";
}

/** Byte count -> "1.4 MB" */
export function bytes(value) {
  if (!Number.isFinite(value) || value < 0) return "—";
  if (value < 1024) return `${value} B`;
  const units = ["KB", "MB", "GB"];
  let size = value / 1024;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(size < 10 ? 1 : 0)} ${units[unit]}`;
}

/** Thousands separators, locale-aware. */
export function count(value) {
  if (!Number.isFinite(value)) return "—";
  return new Intl.NumberFormat().format(Math.round(value));
}

export function seconds(value) {
  if (!Number.isFinite(value)) return "—";
  return value < 10 ? `${value.toFixed(2)}s` : `${value.toFixed(1)}s`;
}

/** File extension badge text: "report.PDF" -> "PDF" */
export function extension(filename = "") {
  const match = /\.([a-z0-9]+)$/i.exec(filename);
  return match ? match[1].toUpperCase() : "DOC";
}

/**
 * Stable 0..1 position for a fingerprint hash.
 *
 * The API returns Python `hash()` values: signed 64-bit integers that exceed
 * Number.MAX_SAFE_INTEGER, so they are treated as opaque labels and folded
 * over their decimal string rather than used in arithmetic.
 */
export function hashToUnit(hash) {
  const text = String(hash);
  let acc = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    acc ^= text.charCodeAt(i);
    acc = Math.imul(acc, 16777619);
  }
  return ((acc >>> 0) % 1000) / 1000;
}

/** Short display form for an opaque hash label. */
export function shortHash(hash) {
  const text = String(hash).replace("-", "");
  return text.slice(0, 8).padStart(8, "0");
}
